/**
 * Outlook / Microsoft Graph reverse phone lookup for call-history enrichment.
 *
 * Dialpad name search uses Graph contacts; Recent Calls enrichment previously
 * stopped at DSS + Directory Search. Outlook-only numbers (e.g. personal contacts)
 * need this path (SPARK-829554 / BEMS01994323).
 */

import { lookupOutlookContactByPhone } from '../../ms-teams-sdk/service/MSTeamsContactService';
import type { IGraphUser } from '../../ms-teams-sdk/service/MsTeamsUserService';
import { isSuffixMatch } from './selector';
import type { EntitySelectionResult } from './types';

/** Collect phone fields from a Graph personal contact. */
export function collectOutlookContactPhones(contact: IGraphUser): string[] {
  const phones: string[] = [];

  if (contact.mobilePhone) {
    phones.push(contact.mobilePhone);
  }
  if (contact.businessPhones?.length) {
    phones.push(...contact.businessPhones);
  }
  if (contact.homePhones?.length) {
    phones.push(...contact.homePhones);
  }

  return phones;
}

/** Pick the Outlook contact whose stored numbers match the call-history phone. */
export function selectOutlookContactForPhone(
  contacts: IGraphUser[],
  phone: string
): IGraphUser | undefined {
  if (!contacts.length) {
    return undefined;
  }

  const matched = contacts.find((contact) =>
    collectOutlookContactPhones(contact).some((candidate) => isSuffixMatch(phone, candidate))
  );

  return matched ?? (contacts.length === 1 ? contacts[0] : undefined);
}

function getOutlookDisplayName(contact: IGraphUser): string {
  const fromDisplay = contact.displayName?.trim();
  if (fromDisplay) {
    return fromDisplay;
  }

  return `${contact.givenName || ''} ${contact.surname || ''}`.trim();
}

/** Resolve display name via Microsoft Graph Outlook contacts (paginated phone scan). */
export async function lookupEntityByOutlook(
  phone: string
): Promise<EntitySelectionResult | undefined> {
  const matches = await lookupOutlookContactByPhone(phone, 10);
  const match = selectOutlookContactForPhone(matches, phone);
  const displayName = match ? getOutlookDisplayName(match) : '';

  if (!match?.id || !displayName) {
    return undefined;
  }

  return {
    displayName,
    entityId: match.id,
    provider: 'outlook',
  };
}
